import { useMemo } from 'react';
import { usePathname } from 'next/navigation';

import {
	getLastPathSegment,
	getParentPath,
	isSamePath,
	pathStartsWith,
	removeTrailingSlash,
} from 'utils/url';

/**
 * The current pathname, plus helpers for comparing it to and deriving from
 * other paths.
 *
 * @example
 *
 * const path = usePath();
 *
 * path.matches(globalLinks.wishlist);
 * path.startsWith(globalLinks.cart);
 * path.lastSegment;
 */
export function usePath() {
	const pathname = usePathname();

	return useMemo(
		() => ({
			pathname,
			lastSegment: getLastPathSegment(pathname),
			parentPath: getParentPath(pathname),
			is: (other: string | undefined) =>
				other ? isSamePath(pathname, other) : false,
			startsWith: (prefix: string | undefined) =>
				prefix ? pathStartsWith(removeTrailingSlash(prefix), pathname) : false,
		}),
		[pathname],
	);
}
